// src/pages/roster/RosterPage.tsx
import React, { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import Sidebar from "../../components/Sidebar";
import { AppHeader } from "../../components/layout/AppHeader";
import PageContainer from "../../components/PageContainer";
import { DoctorAPI } from "../../services/doctor.service";
import type { DoctorRosterItem, AvailabilitySchedule } from "../../services/doctor.service";
import { OnboardingAPI } from "../../services/onboarding.service";
import { useRole } from "../../hooks/useRole";
import { useAppointmentConfig } from "../../hooks/useAppointmentConfig";
import { toast } from "react-toastify";
import { actionErrorMessage } from "../../utils/network";

// ─── Week helpers ──────────────────────────────────────────────────────────────

function getWeekDays(offset = 0): { day: string; date: string; fullDate: Date; isWeekend: boolean }[] {
  const today = new Date();
  const monday = new Date(today);
  const dow = today.getDay();
  const diffToMon = dow === 0 ? -6 : 1 - dow;
  monday.setDate(today.getDate() + diffToMon + offset * 7);
  const DAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
  const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
  return DAYS.map((day, i) => {
    const d = new Date(monday);
    d.setDate(monday.getDate() + i);
    return { day, date: `${MONTHS[d.getMonth()]} ${d.getDate()}`, fullDate: d, isWeekend: i >= 5 };
  });
}

function getWeekLabel(days: ReturnType<typeof getWeekDays>): string {
  const first = days[0].fullDate;
  const jan1 = new Date(first.getFullYear(), 0, 1);
  const weekNum = Math.ceil((((first.getTime() - jan1.getTime()) / 86400000) + jan1.getDay() + 1) / 7);
  return `Week ${weekNum}`;
}

function isToday(d: Date): boolean {
  const now = new Date();
  return d.getDate() === now.getDate() && d.getMonth() === now.getMonth() && d.getFullYear() === now.getFullYear();
}

// ─── Local doctor shape ────────────────────────────────────────────────────────

interface Doctor {
  id: string;
  displayId?: string;
  initials: string;
  name: string;
  specialty: string;
  status: "Present" | "On Leave" | "Not Available";
  rating: number;
  patients: number;
  fees: string;
  todayCompleted: number;
  todayUpcoming: number;
  qualifications: string;
  experience: string;
  location: string;
  department: string;
  phone: string;
  email: string;
}

const STATUS_BADGE: Record<string, { label: string; cls: string }> = {
  Present:        { label: "Present",       cls: "text-green-600 bg-green-50 border-green-200" },
  Break:          { label: "Break",         cls: "text-yellow-600 bg-yellow-50 border-yellow-200" },
  "On Leave":     { label: "On Leave",      cls: "text-red-500 bg-red-50 border-red-200" },
  Absent:         { label: "Absent",        cls: "text-red-500 bg-red-50 border-red-200" },
  "Not Available":{ label: "Not Available", cls: "text-slate-500 bg-slate-100 border-slate-200" },
};

const StatusBadge: React.FC<{ status: string }> = ({ status }) => {
  const s = STATUS_BADGE[status] ?? { label: status, cls: "text-slate-500 bg-slate-100 border-slate-200" };
  return <span className={`text-xs font-medium border px-2 py-0.5 rounded-full ${s.cls}`}>{s.label}</span>;
};

function getInitials(name: string): string {
  return name.replace(/^Dr\.\s*/i, "").split(" ").map((w) => w[0]).join("").slice(0, 2).toUpperCase();
}

function fmtShortRange(start: string, end: string): string {
  const fmt = (t: string) => {
    const [h, m = "00"] = t.split(":");
    return `${h.padStart(2, "0")}:${m.padStart(2, "0")}`;
  };
  return `${fmt(start)}-${fmt(end)}`;
}

function toDoctor(item: DoctorRosterItem): Doctor {
  const DAYS_SHORT = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
  const statusMap: Record<string, Doctor["status"]> = {
    Present: "Present", Break: "Present", "On Leave": "On Leave", Absent: "On Leave", "Not Available": "Not Available",
  };
  const uniqueDayIndices = [...new Set(item.availability_schedules.map(s => s.day_of_week))].sort((a, b) => a - b);
  const schedDays = uniqueDayIndices.map(d => DAYS_SHORT[d]).filter(Boolean);
  const schedStr = item.experience_years
    ? `${item.experience_years} yrs exp${schedDays.length ? ` • ${schedDays.join(", ")}` : ""}`
    : schedDays.length ? schedDays.join(", ") : "Schedule not set";

  return {
    id: item.doctor_id,
    displayId: item.display_id,
    initials: getInitials(item.name),
    name: item.name,
    specialty: item.specialization,
    status: statusMap[item.status] ?? "Present",
    rating: 0, patients: 0,
    fees: item.consultation_fee ? `₹${item.consultation_fee}` : "—",
    todayCompleted: 0, todayUpcoming: 0,
    qualifications: item.qualifications ?? "",
    experience: schedStr,
    location: item.room_location ?? "",
    department: item.department,
    phone: item.phone ?? "",
    email: item.email ?? "",
  };
}

// ─── Shift logic ───────────────────────────────────────────────────────────────

type ShiftKind = "morning" | "afternoon" | "evening" | "add" | "off";
interface Shift { kind: ShiftKind; shortTimes?: string[]; }

// FIX: check schedule FIRST — only fall back to "off" for weekends with NO schedule
function getShift(item: DoctorRosterItem, dayIndex: number, isWeekend: boolean): Shift {
  const dayScheds = item.availability_schedules.filter((s) => s.day_of_week === dayIndex);
  if (dayScheds.length === 0) return isWeekend ? { kind: "off" } : { kind: "add" };
  const firstHour = parseInt(dayScheds[0].start_time.split(":")[0]);
  const kind: ShiftKind = firstHour < 12 ? "morning" : firstHour < 17 ? "afternoon" : "evening";
  // One entry per time window — rendered as separate lines in ShiftCell so multiple
  // windows in a day (e.g. a split morning/evening shift) stay legible instead of
  // running together as one joined string.
  return { kind, shortTimes: dayScheds.map(s => fmtShortRange(s.start_time, s.end_time)) };
}

const SHIFT_BG: Record<string, string> = {
  morning:   "bg-gradient-to-br from-orange-400 to-amber-500",
  afternoon: "bg-gradient-to-br from-cyan-400 to-blue-500",
  evening:   "bg-gradient-to-br from-pink-500 to-fuchsia-500",
};

// ─── ShiftCell ─────────────────────────────────────────────────────────────────

const ShiftCell: React.FC<{
  shift: Shift;
  canEdit: boolean;
  onAdd?: () => void;
  onEdit?: () => void;
}> = ({ shift, canEdit, onAdd, onEdit }) => {
  if (shift.kind === "off") return (
    <div className="flex items-center justify-center h-full py-2">
      <span className="text-xs text-slate-300 font-medium">Off</span>
    </div>
  );
  if (shift.kind === "add") return (
    <div className="flex items-center justify-center py-1.5">
      {canEdit ? (
        <button
          onClick={onAdd}
          className="w-full py-2.5 rounded-xl border border-slate-200 text-xs text-slate-400 font-medium hover:bg-slate-50 transition-colors"
        >
          + Add
        </button>
      ) : (
        <span className="text-xs text-slate-200">—</span>
      )}
    </div>
  );
  return (
    <div className="py-1.5">
      <div
        className={`rounded-xl ${SHIFT_BG[shift.kind]} px-3 py-2.5 ${canEdit ? "cursor-pointer hover:opacity-90" : ""}`}
        onClick={canEdit ? onEdit : undefined}
      >
        <div className="flex items-start gap-1.5">
          <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" className="mt-0.5 shrink-0">
            <circle cx="12" cy="12" r="10" /><polyline points="12 6 12 12 16 14" />
          </svg>
          {/* One line per time window (instead of one joined string) so a doctor
              with a split shift, e.g. 09-14 + 15-18, stays legible instead of
              running together as "09-14, 15-18". */}
          <div className="flex flex-col gap-0.5 min-w-0">
            {shift.shortTimes?.map((t, i) => (
              <span key={i} className="text-white text-xs font-semibold leading-tight whitespace-nowrap">{t}</span>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

// ─── ScheduleEditModal ─────────────────────────────────────────────────────────
// Opens when admin/doctor clicks + Add or an existing shift cell.
// Shows ONLY the schedule table for that doctor — no profile fields.

interface DaySlot {
  start_time: string;
  end_time: string;
  slot_duration_minutes: number;
  max_daily_bookings: number;
}

interface ScheduleRow {
  day_of_week: number;
  enabled: boolean;
  slots: DaySlot[];
}

const DAY_NAMES = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];

interface ScheduleDefaults {
  slot_duration_minutes: number;
  max_daily_bookings: number;
}

function buildScheduleRows(schedules: AvailabilitySchedule[], defaults: ScheduleDefaults): ScheduleRow[] {
  const byDay: Record<number, AvailabilitySchedule[]> = {};
  schedules.forEach((s) => {
    if (!byDay[s.day_of_week]) byDay[s.day_of_week] = [];
    byDay[s.day_of_week].push(s);
  });
  return DAY_NAMES.map((_, i) => ({
    day_of_week: i,
    enabled: !!byDay[i],
    slots: byDay[i]
      ? byDay[i].map((s) => ({
          start_time: s.start_time.slice(0, 5),
          end_time: s.end_time.slice(0, 5),
          slot_duration_minutes: s.slot_duration_minutes ?? defaults.slot_duration_minutes,
          max_daily_bookings: s.max_daily_bookings ?? defaults.max_daily_bookings,
        }))
      : [{ start_time: "09:00", end_time: "17:00", slot_duration_minutes: defaults.slot_duration_minutes, max_daily_bookings: defaults.max_daily_bookings }],
  }));
}

interface ScheduleEditModalProps {
  doctor: Doctor;
  initialSchedules: AvailabilitySchedule[];
  // When set, the modal pre-selects and expands that day
  focusDay?: number;
  onClose: () => void;
  onSaved: (doctorId: string, schedules: AvailabilitySchedule[]) => void;
}

const ScheduleEditModal: React.FC<ScheduleEditModalProps> = ({
  doctor, initialSchedules, focusDay, onClose, onSaved,
}) => {
  // Clinic-wide defaults (slot duration + daily cap) seed any day the doctor
  // hasn't set yet. Seeded synchronously from cache so they're ready on open.
  const clinicConfig = useAppointmentConfig();
  const [rows, setRows] = useState<ScheduleRow[]>(() => {
    const built = buildScheduleRows(initialSchedules, {
      slot_duration_minutes: clinicConfig.slot_duration_minutes,
      max_daily_bookings:    clinicConfig.max_daily_bookings,
    });
    // Pre-enable the focused day if it wasn't already
    if (focusDay !== undefined) {
      built[focusDay].enabled = true;
    }
    return built;
  });
  const [saving, setSaving] = useState(false);

  // Close on Escape
  useEffect(() => {
    const h = (e: KeyboardEvent) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", h);
    return () => document.removeEventListener("keydown", h);
  }, [onClose]);

  const toggleDay = (dayIdx: number, enabled: boolean) =>
    setRows((prev) => prev.map((r, i) => i === dayIdx ? { ...r, enabled } : r));

  const updateSlot = (dayIdx: number, slotIdx: number, field: keyof DaySlot, value: any) =>
    setRows((prev) => prev.map((r, i) => i !== dayIdx ? r : {
      ...r,
      slots: r.slots.map((s, j) => j === slotIdx ? { ...s, [field]: value } : s),
    }));

  const addSlot = (dayIdx: number) =>
    setRows((prev) => prev.map((r, i) => i !== dayIdx ? r : (() => {
      const last = r.slots[r.slots.length - 1];
      const [h, m] = last.end_time.split(":").map(Number);
      const endH = Math.min(h + 1, 23);
      const pad = (n: number) => String(n).padStart(2, "0");
      return { ...r, slots: [...r.slots, { start_time: last.end_time, end_time: `${pad(endH)}:${pad(m)}`, slot_duration_minutes: last.slot_duration_minutes, max_daily_bookings: last.max_daily_bookings }] };
    })()));

  const removeSlot = (dayIdx: number, slotIdx: number) =>
    setRows((prev) => prev.map((r, i) => i !== dayIdx ? r : {
      ...r,
      slots: r.slots.filter((_, j) => j !== slotIdx),
    }));

  const validateRows = (): string | null => {
    for (const r of rows) {
      if (!r.enabled) continue;
      for (const slot of r.slots) {
        if (slot.end_time <= slot.start_time)
          return `${DAY_NAMES[r.day_of_week]}: end time must be after start time`;
      }
      const sorted = [...r.slots].sort((a, b) => a.start_time.localeCompare(b.start_time));
      for (let i = 1; i < sorted.length; i++) {
        if (sorted[i].start_time < sorted[i - 1].end_time)
          return `${DAY_NAMES[r.day_of_week]}: time windows overlap`;
      }
    }
    return null;
  };

  const handleSave = async () => {
    const err = validateRows();
    if (err) { toast.error(err); return; }
    setSaving(true);
    try {
      const payload = rows.filter((r) => r.enabled).flatMap((r) =>
        r.slots.map((slot) => ({
          day_of_week: r.day_of_week,
          start_time: `${slot.start_time}:00`,
          end_time: `${slot.end_time}:00`,
          slot_duration_minutes: slot.slot_duration_minutes,
          max_daily_bookings: slot.max_daily_bookings,
        }))
      );
      const updated = await DoctorAPI.update(doctor.id, { availability_schedules: payload });
      toast.success(`Schedule updated for ${doctor.name}`);
      onSaved(doctor.id, updated.availability_schedules);
      onClose();
    } catch (err) {
      toast.error(actionErrorMessage(err, "Failed to save schedule"));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/40" onClick={onClose}>
      <div
        className="bg-white rounded-2xl shadow-2xl w-full min-w-0 max-w-2xl mx-4 max-h-[90vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100">
          <div>
            <h2 className="text-base font-semibold text-slate-900">Edit Schedule</h2>
            <p className="text-xs text-slate-500 mt-0.5">
              {doctor.name} — {doctor.specialty}
            </p>
          </div>
          <button onClick={onClose} className="p-2 rounded-xl text-slate-400 hover:text-slate-600 hover:bg-slate-100 transition-colors">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M18 6 6 18"/><path d="m6 6 12 12"/>
            </svg>
          </button>
        </div>

        {/* Schedule rows */}
        <div className="overflow-y-auto flex-1 px-6 py-4 flex flex-col gap-2">
          <p className="text-xs text-slate-400 mb-1">Toggle days on/off, set working hours, slot length, and the max bookings per day for each. Use "+ Window" to add a break/split shift.</p>
          {rows.map((row, dayIdx) => (
            <div
              key={dayIdx}
              className={`rounded-xl transition-colors ${
                row.enabled ? "bg-indigo-50 border border-indigo-100" : "bg-slate-50 border border-transparent"
              } ${focusDay === dayIdx ? "ring-2 ring-indigo-300" : ""}`}
            >
              {/* Day header row */}
              <div className="flex items-center gap-3 py-2 px-3">
                <input
                  type="checkbox"
                  checked={row.enabled}
                  onChange={(e) => toggleDay(dayIdx, e.target.checked)}
                  className="w-4 h-4 accent-indigo-600 cursor-pointer flex-shrink-0"
                />
                <span className={`text-sm w-24 font-medium flex-shrink-0 ${row.enabled ? "text-slate-800" : "text-slate-400"}`}>
                  {DAY_NAMES[dayIdx]}
                </span>
                {row.enabled && (
                  <button
                    type="button"
                    onClick={() => addSlot(dayIdx)}
                    className="ml-auto text-xs text-indigo-600 hover:text-indigo-800 font-medium flex items-center gap-1"
                  >
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M12 5v14M5 12h14"/></svg>
                    Window
                  </button>
                )}
              </div>
              {/* Slot rows */}
              {row.slots.map((slot, slotIdx) => (
                <div key={slotIdx} className="flex flex-wrap items-center gap-x-3 gap-y-2 px-3 pb-2 pl-10">
                  <input
                    type="time"
                    value={slot.start_time}
                    disabled={!row.enabled}
                    onChange={(e) => updateSlot(dayIdx, slotIdx, "start_time", e.target.value)}
                    className="border border-slate-200 rounded-lg px-2 py-1.5 text-base sm:text-sm bg-white disabled:opacity-40 disabled:cursor-not-allowed focus:outline-none focus:ring-2 focus:ring-indigo-200 w-24"
                  />
                  <span className="text-slate-400 text-xs flex-shrink-0">to</span>
                  <input
                    type="time"
                    value={slot.end_time}
                    disabled={!row.enabled}
                    onChange={(e) => updateSlot(dayIdx, slotIdx, "end_time", e.target.value)}
                    className="border border-slate-200 rounded-lg px-2 py-1.5 text-base sm:text-sm bg-white disabled:opacity-40 disabled:cursor-not-allowed focus:outline-none focus:ring-2 focus:ring-indigo-200 w-24"
                  />
                  <div className="ml-auto flex items-center gap-2 flex-shrink-0">
                    <select
                      value={slot.slot_duration_minutes}
                      disabled={!row.enabled}
                      onChange={(e) => updateSlot(dayIdx, slotIdx, "slot_duration_minutes", parseInt(e.target.value))}
                      className="border border-slate-200 rounded-lg px-2 py-1.5 text-base sm:text-sm bg-white disabled:opacity-40 disabled:cursor-not-allowed focus:outline-none focus:ring-2 focus:ring-indigo-200"
                      title="Slot length"
                    >
                      {[10, 15, 20, 30, 45, 60].map((m) => (
                        <option key={m} value={m}>{m} min</option>
                      ))}
                    </select>
                    <input
                      type="number"
                      min={1}
                      value={slot.max_daily_bookings}
                      disabled={!row.enabled}
                      onChange={(e) => updateSlot(dayIdx, slotIdx, "max_daily_bookings", Math.max(1, parseInt(e.target.value) || 1))}
                      className="border border-slate-200 rounded-lg px-2 py-1.5 text-base sm:text-sm bg-white w-16 disabled:opacity-40 disabled:cursor-not-allowed focus:outline-none focus:ring-2 focus:ring-indigo-200"
                      title="Max bookings per day"
                    />
                    <span className={`text-xs flex-shrink-0 ${row.enabled ? "text-slate-400" : "text-slate-300"}`}>/day</span>
                    {row.slots.length > 1 && (
                      <button
                        type="button"
                        onClick={() => removeSlot(dayIdx, slotIdx)}
                        className="text-slate-300 hover:text-red-400 transition-colors ml-1"
                        title="Remove window"
                      >
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M18 6 6 18"/><path d="m6 6 12 12"/></svg>
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          ))}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-slate-100">
          <button onClick={onClose} disabled={saving} className="px-4 py-2 text-sm font-medium rounded-xl border border-slate-200 text-slate-600 hover:bg-slate-50 transition-colors disabled:opacity-50">
            Cancel
          </button>
          <button onClick={handleSave} disabled={saving} className="px-5 py-2 text-sm font-medium rounded-xl bg-indigo-600 text-white hover:bg-indigo-700 transition-colors disabled:opacity-60 flex items-center gap-2">
            {saving ? (
              <>
                <svg className="animate-spin w-3.5 h-3.5" viewBox="0 0 24 24" fill="none">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z"/>
                </svg>
                Saving...
              </>
            ) : "Save Schedule"}
          </button>
        </div>
      </div>
    </div>
  );
};

// ─── EditDoctorForm (status + basic info) ──────────────────────────────────────

const inputCls = "w-full h-9 px-3 rounded-xl border border-slate-200 text-base sm:text-sm text-slate-800 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-indigo-200 focus:border-indigo-400 transition-colors";

interface EditDoctorFormProps {
  doc: Doctor;
  onSave: (updated: Doctor) => void;
  onCancel: () => void;
}

const EditDoctorForm: React.FC<EditDoctorFormProps> = ({ doc, onSave, onCancel }) => {
  const [form, setForm] = useState<Doctor>({ ...doc });
  const [saving, setSaving] = useState(false);
  const set = <K extends keyof Doctor>(key: K, value: Doctor[K]) =>
    setForm((f) => ({ ...f, [key]: value }));

  const handleSave = async () => {
    setSaving(true);
    try {
      await DoctorAPI.update(doc.id, {
        name: form.name,
        specialization: form.specialty,
        department: form.department,
        qualifications: form.qualifications || undefined,
        room_location: form.location || undefined,
        phone: form.phone || undefined,
        status: form.status,
      });
      onSave(form);
    } catch {
      // Apply optimistic update even if API fails
      onSave(form);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/40" onClick={onCancel}>
      <div className="bg-white rounded-2xl shadow-2xl w-full min-w-0 max-w-lg mx-4 max-h-[90vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100">
          <div>
            <h2 className="text-base font-semibold text-slate-900">Edit Doctor Profile</h2>
            <p className="text-xs text-slate-500 mt-0.5">{doc.name} — {doc.specialty}</p>
          </div>
          <button onClick={onCancel} className="p-2 rounded-xl text-slate-400 hover:text-slate-600 hover:bg-slate-100 transition-colors">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M18 6 6 18"/><path d="m6 6 12 12"/>
            </svg>
          </button>
        </div>
        <div className="overflow-y-auto flex-1 px-6 py-4 flex flex-col gap-3">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {([
              { label: "Name",                  key: "name" as const,           placeholder: "Dr. Rajesh Kumar" },
              { label: "Specialty",             key: "specialty" as const,      placeholder: "Cardiologist" },
              { label: "Department",            key: "department" as const,     placeholder: "" },
              { label: "Qualifications",        key: "qualifications" as const, placeholder: "" },
              { label: "Experience & Schedule", key: "experience" as const,     placeholder: "10 yrs exp • Mon-Fri, 9-5" },
              { label: "Location / Room",       key: "location" as const,       placeholder: "Room 201" },
              { label: "Phone",                 key: "phone" as const,          placeholder: "" },
              { label: "Consultation Fees",     key: "fees" as const,           placeholder: "₹200" },
            ] as { label: string; key: keyof Doctor; placeholder: string }[]).map(({ label, key, placeholder }) => (
              <div key={key} className="flex flex-col gap-1">
                <label className="text-xs font-medium text-slate-600">{label}</label>
                <input className={inputCls} value={String(form[key] ?? "")} onChange={(e) => set(key, e.target.value as any)} placeholder={placeholder} />
              </div>
            ))}
            <div className="flex flex-col gap-1">
              <label className="text-xs font-medium text-slate-600">Email</label>
              <input className={inputCls + " bg-slate-50 text-slate-400 cursor-not-allowed"} value={form.email} disabled />
            </div>
            <div className="flex flex-col gap-1">
              <label className="text-xs font-medium text-slate-600">Status</label>
              <select className={inputCls + " cursor-pointer"} value={form.status} onChange={(e) => set("status", e.target.value as Doctor["status"])}>
                <option value="Present">Present</option>
                <option value="Break">Break</option>
                <option value="On Leave">On Leave</option>
                <option value="Absent">Absent</option>
                <option value="Not Available">Not Available</option>
              </select>
            </div>
          </div>
        </div>
        <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-slate-100">
          <button onClick={onCancel} disabled={saving} className="px-4 py-2 text-sm font-medium rounded-xl border border-slate-200 text-slate-600 hover:bg-slate-50 disabled:opacity-50">Cancel</button>
          <button onClick={handleSave} disabled={saving} className="px-5 py-2 text-sm font-medium rounded-xl bg-indigo-600 text-white hover:bg-indigo-700 disabled:opacity-60 flex items-center gap-2">
            {saving ? "Saving..." : "Save Changes"}
          </button>
        </div>
      </div>
    </div>
  );
};

// ─── DoctorModal ───────────────────────────────────────────────────────────────

interface DoctorModalProps {
  doc: Doctor;
  rosterItem: DoctorRosterItem | undefined;
  // A doctor may edit only their OWN schedule; admins edit anyone's.
  canEditSchedule: boolean;
  // Profile fields (name, fees, status, …) stay admin-only.
  canEditProfile: boolean;
  onClose: () => void;
  onEdit: (updated: Doctor) => void;
  onScheduleSaved: (doctorId: string, schedules: AvailabilitySchedule[]) => void;
}

const DoctorModal: React.FC<DoctorModalProps> = ({ doc, rosterItem, canEditSchedule, canEditProfile, onClose, onEdit, onScheduleSaved }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [isEditingSchedule, setIsEditingSchedule] = useState(false);

  return (
    <>
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40" onClick={onClose}>
        <div className="bg-white rounded-3xl shadow-2xl w-full min-w-0 max-w-[600px] mx-4 overflow-hidden max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
          <div className="px-6 pt-6 pb-4 flex flex-wrap items-start justify-between gap-3">
            <div className="flex items-center gap-3 min-w-0">
              <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-cyan-400 to-blue-500 flex items-center justify-center text-white text-lg font-bold flex-shrink-0">
                {doc.initials}
              </div>
              <div className="min-w-0">
                <p className="text-base font-bold text-slate-900 truncate">{doc.name}</p>
                <p className="text-sm text-slate-500 truncate">{doc.specialty}</p>
                <div className="mt-1.5">
                  <StatusBadge status={doc.status} />
                </div>
              </div>
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              {/* Schedule edit — admin or the doctor themselves */}
              {canEditSchedule && (
                <button
                  onClick={() => setIsEditingSchedule(true)}
                  className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl border border-cyan-400 text-cyan-500 text-xs font-medium hover:bg-cyan-50 transition-colors"
                >
                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <rect x="3" y="4" width="18" height="18" rx="2"/>
                    <line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/>
                  </svg>
                  Schedule
                </button>
              )}
              {/* Profile edit — admin only */}
              {canEditProfile && (
                <button
                  onClick={() => setIsEditing(true)}
                  className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl border border-cyan-400 text-cyan-500 text-xs font-medium hover:bg-cyan-50 transition-colors"
                >
                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/>
                    <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/>
                  </svg>
                  Edit
                </button>
              )}
              <button onClick={onClose} className="p-1.5 text-slate-400 hover:text-slate-600">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M18 6 6 18"/><path d="m6 6 12 12"/>
                </svg>
              </button>
            </div>
          </div>

          <div className="px-6 pb-6 flex flex-col gap-4">
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              {[
                { value: doc.rating ? doc.rating.toFixed(1) : "—", label: "Rating" },
                { value: doc.patients ? doc.patients.toString() : "—", label: "Patients" },
                { value: doc.fees, label: "Fees" },
              ].map((s) => (
                <div key={s.label} className="bg-white border border-slate-100 rounded-2xl py-3 flex flex-col items-center gap-1 shadow-sm">
                  <p className="text-lg font-bold text-slate-900">{s.value}</p>
                  <p className="text-xs text-slate-400">{s.label}</p>
                </div>
              ))}
            </div>

            <div className="bg-cyan-50 rounded-2xl px-4 py-3 flex items-center justify-between">
              <div>
                <p className="text-sm font-semibold text-slate-800">Today's Patients</p>
                <p className="text-xs text-slate-400 mt-0.5">{doc.todayCompleted} completed • {doc.todayUpcoming} upcoming</p>
              </div>
              <span className="text-2xl font-bold text-cyan-500">{doc.todayCompleted + doc.todayUpcoming}</span>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="min-w-0">
                <p className="text-sm font-semibold text-slate-800 mb-3">Professional Details</p>
                <div className="flex flex-col gap-3 text-xs">
                  {[
                    { label: "Qualifications", value: doc.qualifications || "—" },
                    { label: "Experience & Schedule", value: doc.experience || "—" },
                    { label: "Location", value: doc.location || "—" },
                    { label: "Department", value: doc.department },
                  ].map((item) => (
                    <div key={item.label}>
                      <p className="text-slate-400">{item.label}</p>
                      <p className="text-slate-700 font-medium mt-0.5 break-words">{item.value}</p>
                    </div>
                  ))}
                </div>
              </div>
              <div className="min-w-0">
                <p className="text-sm font-semibold text-slate-800 mb-3">Contact</p>
                <div className="flex flex-col gap-2 text-xs text-slate-700">
                  <p className="break-words">{doc.phone || "—"}</p>
                  <p className="break-words">{doc.email || "—"}</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {isEditing && (
        <EditDoctorForm
          doc={doc}
          onSave={(updated) => { onEdit(updated); setIsEditing(false); }}
          onCancel={() => setIsEditing(false)}
        />
      )}

      {isEditingSchedule && (
        <ScheduleEditModal
          doctor={doc}
          initialSchedules={rosterItem?.availability_schedules ?? []}
          onClose={() => setIsEditingSchedule(false)}
          onSaved={(id, schedules) => { onScheduleSaved(id, schedules); setIsEditingSchedule(false); }}
        />
      )}
    </>
  );
};

// ─── RosterPage ────────────────────────────────────────────────────────────────

const RosterPage: React.FC = () => {
  const [rosterItems, setRosterItems] = useState<DoctorRosterItem[]>([]);
  const [doctors, setDoctors] = useState<Doctor[]>([]);
  const [loading, setLoading] = useState(true);
  const [modalDoctor, setModalDoctor] = useState<Doctor | null>(null);
  const [searchParams] = useSearchParams();
  const focusDoctorId = searchParams.get("doctorId");
  const [highlightedId, setHighlightedId] = useState<string | null>(null);
  const [weekOffset, setWeekOffset] = useState(0);

  // Role-based access: only Admin and the doctor themselves can edit
  const [currentRole, setCurrentRole] = useState<string | null>(null);
  const [currentUserId, setCurrentUserId] = useState<string | null>(null);

  // Schedule editor state — which doctor+day was clicked
  const [scheduleModal, setScheduleModal] = useState<{
    doctor: Doctor;
    rosterItem: DoctorRosterItem | undefined;
    focusDay?: number;
  } | null>(null);

  const weekDays = getWeekDays(weekOffset);
  const weekLabel = getWeekLabel(weekDays);
  const scheduledDayIndices = new Set(
  rosterItems.flatMap((r) => r.availability_schedules.map((s) => s.day_of_week))
);


  // Mirrors PUT /doctors/{id} server-side: Manage Doctors edits anyone's
  // schedule and profile; Manage Doctor Schedule edits only your own schedule.
  // Legacy fallbacks are the old role rules (Admin anyone, Doctor own row).
  const { canOr } = useRole();
  const isAdmin = canOr("doctors.manage", currentRole === "Admin");
  const canEditOwnSchedule = canOr("doctors.manage_schedule", currentRole === "Doctor" || currentRole === "Admin");
  const canEditSchedule = (doctorId: string) => {
    if (isAdmin) return true;
    if (canEditOwnSchedule && currentUserId === doctorId) return true;
    return false;
  };

  // Fetch role/identity
  useEffect(() => {
    OnboardingAPI.getMe()
      .then((me: any) => {
        setCurrentRole(me.role ?? "");
        setCurrentUserId(me.doctor_id ?? me.user_id ?? null);
      })
      .catch(() => setCurrentRole(""));
  }, []);

  // Fetch roster
  useEffect(() => {
    DoctorAPI.getRoster()
      .then((items) => {
        setRosterItems(items);
        const mapped = items.map(toDoctor);
        setDoctors(mapped);
        if (focusDoctorId) {
          const found = mapped.find((d) => d.id === focusDoctorId || d.displayId === focusDoctorId);
          if (found) {
            setHighlightedId(found.id);
            setModalDoctor(found);
          }
        }
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [focusDoctorId]); // eslint-disable-line react-hooks/exhaustive-deps

  // When schedule is saved, update the rosterItems array so the grid re-renders
  const handleScheduleSaved = (doctorId: string, newSchedules: AvailabilitySchedule[]) => {
    setRosterItems((prev) =>
      prev.map((r) =>
        r.doctor_id === doctorId ? { ...r, availability_schedules: newSchedules } : r
      )
    );
    // Also refresh the doctor's experience summary label
    setDoctors((prev) =>
      prev.map((d) => {
        if (d.id !== doctorId) return d;
        const rItem = rosterItems.find((r) => r.doctor_id === doctorId);
        if (!rItem) return d;
        return toDoctor({ ...rItem, availability_schedules: newSchedules });
      })
    );
  };

  const handleEdit = (updated: Doctor) => {
    setDoctors((prev) => prev.map((d) => d.id === updated.id ? updated : d));
    setModalDoctor(updated);
    window.dispatchEvent(new Event("doctorUpdated"));
  };

  return (
    <div className="flex min-h-screen bg-[#f5f5f7]">
      {/* Doctor profile modal */}
      {modalDoctor && (
        <DoctorModal
          doc={modalDoctor}
          rosterItem={rosterItems.find((r) => r.doctor_id === modalDoctor.id)}
          canEditSchedule={canEditSchedule(modalDoctor.id)}
          canEditProfile={isAdmin}
          onClose={() => setModalDoctor(null)}
          onEdit={handleEdit}
          onScheduleSaved={handleScheduleSaved}
        />
      )}

      {/* Schedule editor opened directly from a grid cell */}
      {scheduleModal && !modalDoctor && (
        <ScheduleEditModal
          doctor={scheduleModal.doctor}
          initialSchedules={scheduleModal.rosterItem?.availability_schedules ?? []}
          focusDay={scheduleModal.focusDay}
          onClose={() => setScheduleModal(null)}
          onSaved={(id, schedules) => { handleScheduleSaved(id, schedules); setScheduleModal(null); }}
        />
      )}

      <Sidebar />

      <div className="flex-1 min-w-0 lg:pl-[var(--sidebar-w)] transition-[padding-left] duration-300 ease-in-out pt-14 lg:pt-0 flex flex-col min-h-screen">
        <AppHeader title="Roster" />
        <div className="p-4 flex flex-1">
        <PageContainer className="flex flex-col lg:flex-row overflow-hidden">

          {/* ── Left: Doctor List ── */}
          <div className="w-full lg:w-[280px] flex-shrink-0 max-h-64 lg:max-h-none border-b lg:border-b-0 lg:border-r border-slate-100 flex flex-col relative">
            <div className="px-4 py-4 flex-shrink-0">
              <h2 className="text-sm font-semibold text-slate-800">
                Doctors ({loading ? "…" : doctors.length})
              </h2>
            </div>

            {/* + button in sidebar — Admin only, links to Doctor Management page */}
            {currentRole === "Admin" && (
              <a
                href="/doctors"
                className="absolute -right-3.5 top-3.5 z-10 w-7 h-7 rounded-full bg-white border border-slate-200 shadow-sm flex items-center justify-center text-slate-500 text-base font-light hover:bg-slate-50 transition-colors"
                title="Manage Doctors"
              >
                +
              </a>
            )}

            <div className="flex-1 overflow-y-auto px-3 pb-3 flex flex-col gap-2">
              {loading ? (
                Array.from({ length: 4 }).map((_, i) => (
                  <div key={i} className="w-full h-20 rounded-2xl bg-slate-100 animate-pulse" />
                ))
              ) : doctors.length === 0 ? (
                <div className="flex flex-col items-center justify-center h-full text-center px-4 py-10">
                  <p className="text-sm text-slate-500 font-medium">No doctors found</p>
                  <p className="text-xs text-slate-400 mt-1">Add doctors from the Doctor Management page</p>
                </div>
              ) : (
                doctors.map((doc) => {
                  const isActive = modalDoctor?.id === doc.id;
                  const isHighlighted = highlightedId === doc.id;
                  return (
                    <button
                      key={doc.id}
                      onClick={() => setModalDoctor(doc)}
                      className={`w-full flex items-center gap-3 px-3 py-3 rounded-2xl border text-left transition-all ${
                        isActive ? "bg-blue-50 border-l-4 border-cyan-400"
                        : isHighlighted ? "bg-cyan-50 border-l-4 border-cyan-300 ring-1 ring-cyan-200"
                        : "bg-white border-slate-100 hover:bg-blue-50"
                      }`}
                    >
                      <div className={`w-10 h-10 rounded-full bg-gradient-to-br from-cyan-400 to-blue-500 flex items-center justify-center text-white text-sm font-bold flex-shrink-0 ${isHighlighted ? "ring-2 ring-cyan-400" : ""}`}>
                        {doc.initials}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-semibold text-slate-800 truncate">{doc.name}</p>
                        <p className="text-xs text-slate-400 mt-0.5">{doc.specialty}</p>
                        <div className="flex items-center gap-2 mt-1.5">
                          <StatusBadge status={doc.status} />
                        </div>
                      </div>
                    </button>
                  );
                })
              )}
            </div>
          </div>

          {/* ── Right: Calendar ── */}
          <div className="flex-1 min-w-0 flex flex-col overflow-hidden">
            <div className="px-4 sm:px-6 py-4 flex flex-wrap items-center justify-between gap-2 border-b border-slate-100 flex-shrink-0">
              <div className="flex items-center gap-2 min-w-0">
                <button onClick={() => setWeekOffset((o) => o - 1)} className="w-7 h-7 flex items-center justify-center rounded-full hover:bg-slate-100 text-slate-500 transition-colors">
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                    <polyline points="15 18 9 12 15 6"/>
                  </svg>
                </button>
                <div>
                  <h2 className="text-base font-semibold text-slate-800">
                    {weekDays[0].fullDate.toLocaleString("default", { month: "long", year: "numeric" })}
                  </h2>
                  <p className="text-xs text-slate-400">
                    {weekLabel} • {weekDays[0].date}–{weekDays[6].date.split(" ")[1]}
                  </p>
                </div>
                <button onClick={() => setWeekOffset((o) => o + 1)} className="w-7 h-7 flex items-center justify-center rounded-full hover:bg-slate-100 text-slate-500 transition-colors">
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                    <polyline points="9 18 15 12 9 6"/>
                  </svg>
                </button>
              </div>
              <button onClick={() => setWeekOffset(0)} className="flex items-center gap-2 px-4 py-2 rounded-xl border border-cyan-400 text-cyan-500 text-sm font-medium hover:bg-cyan-50 transition-colors">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <rect x="3" y="4" width="18" height="18" rx="2"/>
                  <line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/>
                </svg>
                Today
              </button>
            </div>

            <div className="flex-1 min-w-0 overflow-auto">
              {loading ? (
                <div className="flex items-center justify-center h-full text-slate-400 text-sm">Loading roster...</div>
              ) : (
                <table className="w-full border-collapse" style={{ minWidth: 640 }}>
                  <thead>
                    <tr className="border-b border-slate-100 bg-slate-50">
                      <th className="w-36 px-4 py-3 text-left text-xs font-semibold text-slate-500">Doctor</th>
                      {weekDays.map((d, i) => (
                        <th key={d.day} className="px-2 py-3 text-center">
                          <div className={`text-sm font-semibold ${isToday(d.fullDate) ? "text-cyan-500" : d.isWeekend && !scheduledDayIndices.has(i) ? "text-slate-300" : "text-slate-600"}`}>
  {d.day}
</div>
<div className={`text-xs mt-0.5 ${isToday(d.fullDate) ? "text-cyan-400" : d.isWeekend && !scheduledDayIndices.has(i) ? "text-slate-300" : "text-slate-400"}`}>
  {d.date}
</div>
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {doctors.length === 0 ? (
                      <tr><td colSpan={8} className="text-center py-16 text-slate-400 text-sm">No doctors in roster</td></tr>
                    ) : (
                      doctors.map((doc) => {
                        const rosterItem = rosterItems.find((r) => r.doctor_id === doc.id);
                        const isHighlighted = highlightedId === doc.id;
                        const canEdit = canEditSchedule(doc.id);
                        return (
                          <tr key={doc.id} className={`border-b border-slate-100 transition-colors ${isHighlighted ? "bg-cyan-50/50" : ""}`}>
                            <td className="px-4 py-2 w-36">
                              <div className="flex items-center gap-2">
                                <div className={`w-8 h-8 rounded-full bg-gradient-to-br from-cyan-400 to-blue-500 flex items-center justify-center text-white text-xs font-bold flex-shrink-0 ${isHighlighted ? "ring-2 ring-cyan-400" : ""}`}>
                                  {doc.initials}
                                </div>
                                <div className="min-w-0">
                                  <p className="text-xs font-semibold text-slate-700 truncate">
                                    {doc.name.replace(/^Dr\.?\s*/i, "").split(" ")[0]}
                                  </p>
                                  <p className="text-xs text-slate-400 truncate">{doc.specialty}</p>
                                </div>
                              </div>
                            </td>
                            {weekDays.map((d, i) => {
                              const shift = rosterItem ? getShift(rosterItem, i, d.isWeekend) : (d.isWeekend ? { kind: "off" as ShiftKind } : { kind: "add" as ShiftKind });
                              return (
                                <td key={i} className="px-1">
                                  <ShiftCell
                                    shift={shift}
                                    canEdit={canEdit}
                                    onAdd={() => setScheduleModal({ doctor: doc, rosterItem, focusDay: i })}
                                    onEdit={() => setScheduleModal({ doctor: doc, rosterItem, focusDay: i })}
                                  />
                                </td>
                              );
                            })}
                          </tr>
                        );
                      })
                    )}
                  </tbody>
                </table>
              )}
            </div>
          </div>

        </PageContainer>
        </div>
      </div>
    </div>
  );
};

export default RosterPage;